import Navbar from '@components/Navbar';
import Banner from '@components/Banner';
import { Outlet, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import BodyLayout from '@components/BodyLayout';
import ScrollToTop from '@hooks/scrollToTop';

import { NotificationPermission, NotificationType, POPUP_CONFIG } from '@constants/notification.constants';
import {
	getNotificationDescHtml,
	handleNotificationPermission,
	initNotification,
	checkStandaloneMode,
	handleGrantedPermission,
} from '@helpers/notification.helper';
import { getCurrentUser } from '@helpers/auth.helper';
import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/state';
import { isDesktop, isMobile } from 'react-device-detect';
import { LocalStorageKey, SessionStorageKey, BooleanString } from '@constants/storage-keys.constants';
import { ROUTES } from '@constants/route.constant';
import { AppState } from '@/state';
import NetworkError from '@components/NetworkError';
import { checkMaintenance } from '@helpers/maintenance.helper';

const Quicklinks = lazy(() => import('@components/QuickLinks'));
const PopUp = lazy(() => import('@components/PopUp'));
const MaintenanceNotice = lazy(() => import('@components/MaintenanceNotice'));
const EnableNotificationGuide = lazy(() => import('../EnableNotificationGuide'));
const NotificationAlert = lazy(() => import('@assets/images/notification.svg'));
const Error = lazy(() => import('@assets/images/error-white.svg'));

export default function Layout() {
	const { pathname } = useLocation();

	const { instance } = useMsal();
	const dispatch = useDispatch<AppDispatch>();
	const user = useSelector((state: AppState) => state.userState.user);
	const maintenanceInfo = useSelector((state: AppState) => state.maintenanceState.firebaseMaintenanceData);

	const [isStandaloneMode, setIsStandaloneMode] = useState(false);
	const [isPopUpHidden, setPopUpHidden] = useState(false);
	const [uid, setUserId] = useState('');
	const [showGuide, setShowGuide] = useState(false);
	const [isNotificationOn, setIsNotificationOn] = useState(false);
	const [notificationType, setNotificationType] = useState(NotificationType.PUSH_NOTIFICATION_ALERT);
	const [isSubscriptionError, setIsSubscriptionError] = useState(false);
	const [triggerNotificationOffAlert, setTriggerNotificationOffAlert] = useState(false);
	const [isFirstNotifGranted, setFirstNotifGranted] = useState(false);
	const [pushNotifEventParamKey, setPushNotifEventParamKey] = useState({ outlet: '', brand: '' });
	const [maintenanceNotice, setMaintenanceNotice] = useState<{
		title: string;
		message: string;
	} | null>(null);

	const hidePopUp = () => {
		if (!isPopUpHidden) {
			setPopUpHidden(true);
			return;
		}
	};

	const dismissPopUp = () => {
		if (!isPopUpHidden) {
			setPopUpHidden(true);
			window.sessionStorage.setItem(SessionStorageKey.HIDE_NOTI_PROMPT, BooleanString.TRUE);
			return;
		}
	};

	const handleTriggerNotificationOffAlert = () => {
		showNotificationOffAlert();
		setTriggerNotificationOffAlert(false);
	};

	const appInstalledHandler = () => setIsStandaloneMode(true);

	const showNotificationOffAlert = () => {
		setNotificationType(NotificationType.NOTIFICATION_OFF);
		setPopUpHidden(false);
	};

	const showEnableNotificationGuide = () => {
		setPopUpHidden(true);
		setShowGuide(true);
	};

	const handleUnsuccessfulSubscription = () => {
		setPopUpHidden(false);
		setIsSubscriptionError(true);
		setIsNotificationOn(false);
		setNotificationType(NotificationType.SUBSCRIPTION_ERROR);
		window.localStorage.removeItem(LocalStorageKey.IS_SUBSCRIBED);
	};

	const resubscribe = () => handleGrantedPermission(uid, setIsNotificationOn, handleUnsuccessfulSubscription, isFirstNotifGranted, pushNotifEventParamKey);

	const dismissGuide = () => setShowGuide(false);

	const notificationAPICheck = () => {
		if (typeof Notification === 'undefined') {
			console.error('No Notification API');
			return false;
		}
		return true;
	};

	const enableNotification = async () => {
		if (!notificationAPICheck()) return;
		if (Notification.permission === NotificationPermission.DENIED) {
			showEnableNotificationGuide();
			return;
		}
		setFirstNotifGranted(true);
		handleNotificationPermission(uid, setIsNotificationOn, handleUnsuccessfulSubscription, showNotificationOffAlert, pushNotifEventParamKey);
	};

	useEffect(() => {
		if (!notificationAPICheck()) return;
		if (user.merchant.brand && Notification.permission === NotificationPermission.DEFAULT) {
			setPopUpHidden(false);
			setPushNotifEventParamKey({
				outlet: user.outletName || user.name,
				brand: user.merchant.brand,
			});
		}
	}, [user.merchant?.brand]);

	useEffect(() => {
		const checkMaintenanceStatus = () => {
			if (!user.merchant.brand) return null;
			return checkMaintenance(maintenanceInfo, user) ?? null;
		};

		setMaintenanceNotice(checkMaintenanceStatus());
	}, [maintenanceInfo, user.merchant?.brand]);

	useEffect(() => {
		if (triggerNotificationOffAlert) {
			handleTriggerNotificationOffAlert();
		}
	}, [triggerNotificationOffAlert]);

	useEffect(() => {
		const standalone = checkStandaloneMode();
		setIsStandaloneMode(standalone);
		window.addEventListener('appinstalled', appInstalledHandler);

		const getUser = async () => {
			const id = await getCurrentUser(instance, dispatch);
			if (id) {
				setUserId(id);
				notificationAPICheck();
				if (Notification.permission !== NotificationPermission.DEFAULT) {
					initNotification(id, setIsNotificationOn, handleUnsuccessfulSubscription, setPopUpHidden);
				}
			}
		};
		void getUser();
	}, []);

	return (
		<div className='text-base mb-[-38px] flex flex-col min-h-screen'>
			{showGuide && (
				<Suspense fallback={null}>
					<EnableNotificationGuide dismiss={dismissGuide} />
				</Suspense>
			)}
			{!isPopUpHidden && ((isMobile && isStandaloneMode) || isDesktop) && (
				<Suspense fallback={null}>
					<PopUp
						icon={isSubscriptionError ? <Error /> : <NotificationAlert />}
						classIcon='w-8 h-8 mt-1'
						bgColor={isSubscriptionError ? 'bg-red' : 'bg-blue'}
						tooltipColor={isSubscriptionError ? 'border-red' : 'border-blue'}
						descriptionHtml={getNotificationDescHtml(notificationType, enableNotification, resubscribe)}
						onClose={hidePopUp}
						onDismiss={dismissPopUp}
						delayTime={POPUP_CONFIG.DELAY_TIME[notificationType]}
						displayDuration={POPUP_CONFIG.DISPLAY_DURATION}
					/>
				</Suspense>
			)}

			<ScrollToTop />
			<Navbar enableNotification={enableNotification} isNotificationOn={isNotificationOn} />
			<NetworkError>
				{!pathname.includes(`/${ROUTES.DEALS_PRIVILEGES}`) && pathname !== '/contact-us' && pathname !== '/qr-code' && pathname !== '/referral' && (
					<Banner />
				)}
				{maintenanceNotice && (
					<Suspense fallback={null}>
						<MaintenanceNotice title={maintenanceNotice?.title} message={maintenanceNotice?.message} />
					</Suspense>
				)}
				{pathname === '/' && (
					<Suspense fallback={null}>
						<Quicklinks />
					</Suspense>
				)}
				<BodyLayout>
					<Outlet context={{ setTriggerNotificationOffAlert }} />
				</BodyLayout>
			</NetworkError>
		</div>
	);
}
